import { supabase } from './supabase';
import type { Currency } from './CurrencyContext';

export type Direction = 'IN' | 'OUT';

export interface LedgerCustomer {
  first_name: string | null;
  last_name: string | null;
  is_banned: boolean | null;
  ban_reason: string | null;
  phone?: string | null;
}

export interface LedgerRow {
  id: string;
  booking_id: number | null;
  customer_id: string;
  car_id: number | null;
  type: string;
  description: string | null;
  amount: number;
  direction: Direction;
  exchange_rate_at_entry: number | null;
  created_at: string | null;
  /** Set by `record_customer_payment`. Such a row is tied to an envelope and a movement. */
  pay_method?: string | null;
  customers: LedgerCustomer | LedgerCustomer[] | null;
  cars: { plate_number: string | null } | { plate_number: string | null }[] | null;
}

interface RateRow { currency: string; rate_to_try: number; }

const LEDGER_COLUMNS = 'id, booking_id, customer_id, car_id, type, description, amount, direction, exchange_rate_at_entry, created_at, pay_method, customers(first_name, last_name, is_banned, ban_reason, phone), cars(plate_number)';
const PAGE_ROWS = 1000;

/**
 * The whole ledger, newest first. The API returns at most 1000 rows however many
 * exist, so it is read page by page: a balance worked out from the first page alone
 * is simply wrong. `id` breaks ties, or a row could land on two pages or on neither.
 */
export async function getLedgerRows(): Promise<LedgerRow[]> {
  const rows: LedgerRow[] = [];
  for (;;) {
    const { data, error } = await supabase
      .from('customer_accounting_ledger')
      .select(LEDGER_COLUMNS)
      .order('created_at', { ascending: false })
      .order('id')
      .range(rows.length, rows.length + PAGE_ROWS - 1);
    if (error) throw error;
    const page = (data ?? []) as unknown as LedgerRow[];
    rows.push(...page.map(row => ({ ...row, amount: Number(row.amount) })));
    if (page.length < PAGE_ROWS) return rows;
  }
}

export function convertEntry(
  row: Pick<LedgerRow, 'amount' | 'exchange_rate_at_entry'>,
  currency: Currency,
  rates: RateRow[],
  fallbackUsdRate: number | null,
): { value: number; approx: boolean } {
  if (currency === 'TRY') return { value: row.amount, approx: false };

  if (currency === 'USD') {
    const stored = row.exchange_rate_at_entry;
    if (stored != null && stored > 0) return { value: row.amount / stored, approx: false };
    if (fallbackUsdRate != null && fallbackUsdRate > 0) return { value: row.amount / fallbackUsdRate, approx: true };
    return { value: 0, approx: true };
  }

  const rate = rates.find(r => r.currency === currency)?.rate_to_try;
  if (rate != null && rate > 0) return { value: row.amount / rate, approx: true };
  return { value: row.amount, approx: true };
}

const firstOf = <T,>(value: T | T[] | null | undefined): T | null => (Array.isArray(value) ? value[0] ?? null : value ?? null);

export interface CustomerBalance {
  customerId: string;
  name: string;
  /** The car on their newest line, as a label only. */
  plate: string | null;
  balance: number;
}

/** One balance per customer, the sum of all their bookings, never split by car. */
export function customerBalances(rows: LedgerRow[], currency: Currency, rates: RateRow[], fallbackUsdRate: number | null): CustomerBalance[] {
  const byCustomer = new Map<string, CustomerBalance & { at: number }>();
  for (const row of rows) {
    let entry = byCustomer.get(row.customer_id);
    if (!entry) {
      const person = firstOf(row.customers);
      entry = { customerId: row.customer_id, name: [person?.first_name, person?.last_name].filter(Boolean).join(' '), plate: null, balance: 0, at: -Infinity };
      byCustomer.set(row.customer_id, entry);
    }
    const at = row.created_at ? Date.parse(row.created_at) || 0 : 0;
    const plate = firstOf(row.cars)?.plate_number ?? null;
    if (plate && at >= entry.at) { entry.plate = plate; entry.at = at; }
    const { value } = convertEntry(row, currency, rates, fallbackUsdRate);
    entry.balance += row.direction === 'IN' ? value : -value;
  }
  return Array.from(byCustomer.values()).map(({ at, ...entry }) => entry);
}

export function ledgerTotals(balances: CustomerBalance[]) {
  const debtors = balances.filter(entry => entry.balance < -0.005).sort((a, b) => a.balance - b.balance);
  const credit = balances.filter(entry => entry.balance > 0.005);
  return {
    debtors,
    owed: debtors.reduce((sum, entry) => sum - entry.balance, 0),
    inCredit: credit.length,
    credit: credit.reduce((sum, entry) => sum + entry.balance, 0),
  };
}
